import { useCallback, useEffect, useState } from "react";

import apiClient from "../../../../../helpers/apiClient";

const PAGE_SIZE = 25;

const useReservationActivityLogs = (reservationId, refreshKey) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(false);

    const fetchPage = useCallback(
        async (pageNum, { append = false } = {}) => {
            if (!reservationId) {
                return;
            }

            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
                setError(false);
            }

            try {
                const { data } = await apiClient.get(`/api/reservation-activity-log/${reservationId}`, {
                    params: {
                        page: pageNum,
                        limit: PAGE_SIZE,
                        sort: "-createdAt",
                    },
                });

                if (data?.success) {
                    const items = Array.isArray(data.data) ? data.data : [];
                    const meta = data.meta ?? {};
                    const totalPage = meta.totalPage ?? 1;
                    const currentPage = meta.page ?? pageNum;

                    setLogs((prev) => (append ? [...prev, ...items] : items));
                    setPage(currentPage);
                    setHasMore(currentPage < totalPage);
                } else {
                    setError(true);
                }
            } catch {
                setError(true);
                if (!append) {
                    setLogs([]);
                }
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [reservationId],
    );

    useEffect(() => {
        setLogs([]);
        setPage(1);
        setHasMore(false);
        fetchPage(1, { append: false });
    }, [fetchPage, refreshKey]);

    const loadMore = useCallback(() => {
        if (loading || loadingMore || !hasMore) {
            return;
        }
        fetchPage(page + 1, { append: true });
    }, [fetchPage, hasMore, loading, loadingMore, page]);

    const refresh = useCallback(() => {
        fetchPage(1, { append: false });
    }, [fetchPage]);

    return {
        logs,
        loading,
        loadingMore,
        hasMore,
        error,
        loadMore,
        refresh,
    };
};

export default useReservationActivityLogs;
